"use client";

import type React from "react";

import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  Share as ShareIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Email as EmailIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
  CloudUpload,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { useUser } from "@/contexts/UserContext";
import axios from "axios";
import { apiRemote, IHost, IWorkshopForm } from "@/api";
import { useAlert } from "@/contexts/AlertContext";
import useDebounce from "@/hooks/use-debounce";
import { EditToggle, EditType } from "@/components/EditToggle";

interface Educator {
  ID: string; // Changed from 'id' to 'ID' to match your data
  user_login: string;
  user_pass: string;
  user_nicename: string;
  user_email: string;
  user_url: string;
  user_registered: string;
  user_activation_key: string;
  user_status: string;
  display_name: string; // This is the name you want to display
  status: string;
}

export default function WorkshopCreation() {
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [aiImagePrompt, setAiImagePrompt] = useState(""); // State for AI image prompt
  const [selectedImage, setSelectedImage] = useState<string | null>(null); // State for selected image URL

  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isGeneratingObjectives, setIsGeneratingObjectives] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const [educators, setEducators] = useState<any[]>([]);
  const [searchEducators, setSearchEducators] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchEducators, 500);
  const [selectedEducator, setSelectedEducator] = useState<Educator | null>(
    null
  );

  const [selectedEducators, setSelectedEducators] = useState<Educator[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  const [topics, setTopics] = useState<any[]>([]);

  const alert = useAlert();

  const navigate = useNavigate();
  const { user } = useUser();

  const initialFormData: IWorkshopForm = {
    title: "",
    description: "",
    learning_objective: "",
    target_audience: "",
    host_type: "pythaverse",
    hosts: [],
    duration: 0,
    format: "",
    category_id: 0,
    max_attendee: 0,
    image: null,
    date: "",
    event_link: "",
    video_link: "",
    discussion_link: "",
    created_by: user?.user_login || "", // Ensure this is correctly initialized based on `user`
    is_public: 1,
    is_featured: 0,
    topics: [],
    attached_documents: [],
    status: "published"
  };
  const isAdminOrOrg = user?.is_admin || user?.is_org;

  if (user?.user_login == null || typeof user?.user_login == "undefined") {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="h5" color="error">
          Please login to create workshop.
        </Typography>
      </Container>
    );
  }

  if (!isAdminOrOrg && user?.role !== "educator") {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="h5" color="error">
          You do not have access to this page, only users with the educator role are allowed to access
        </Typography>
      </Container>
    );
  }

  const [formData, setFormData] = useState<IWorkshopForm>(initialFormData);
  useEffect(() => {
    if (searchEducators == "") {
      setEducators([]);
    } else {
      apiRemote
        .getListEducator(searchEducators)
        .then((data) => {
          setEducators(data.data || []);
        })
        .catch((error) => {
          console.error("Error fetching educators:", error);
          setEducators([]); // Clear educators on error
        });
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    // Nếu host_type là "guest", reset mảng selectedEducators
    if (formData.host_type === "guest") {
        setSelectedEducators([]);
    }
  }, [formData.host_type]);

  useEffect(() => {
    if (formData.host_type === "pythaverse") {
      const newHosts: IHost[] = selectedEducators.map(edu => ({
          host_name: edu.display_name,
          host_email: edu.user_email,
          host_organization: "",
          host_bio: ""
      }));
      setFormData(prev => ({ ...prev, hosts: newHosts }));
    }
  }, [selectedEducators, formData.host_type]);

  useEffect(() => {
    apiRemote
      .getListCategory()
      .then((data) => {
        setCategories(data.data || []);
      })
      .catch((error) => {
        console.error("Error fetching educators:", error);
        setCategories([]); // Clear educators on error
      });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // alert.success("Workshop Published!")
    if (
      !formData.title ||
      !formData.description ||
      !formData.date ||
      !formData.topics ||
      formData.topics.length === 0 ||
      !formData.duration ||
      !formData.format ||
      !formData.category_id ||
      !formData.max_attendee ||
      !formData.learning_objective ||
      !formData.target_audience
    ) {
      alert.error("Please fill in all required information");
      return;
    }

    if (!formData.image) {
      alert.error("Please upload an image for the new workshop.");
      return;
    }
    const dataToSend = { ...formData, status: "published" };
    console.log("Submitting workshop with data:", dataToSend);
    setIsSubmitting(true);
    if (isAdminOrOrg) {
      apiRemote
        .createWorkshop(dataToSend)
        .then((data) => {
          if (data.code == "201") {
            alert.success(data.message);

            setFormData(initialFormData);
            setSelectedImage(null); // Clear selected image
            setAiImagePrompt(""); // Clear AI image prompt
            setSelectedEducator(null); // Clear selected educator
            setSearchEducators("");
            setSelectedEducators([]); // Clear selected educators
          } else {
            alert.error(data.message);
          }
        })
        .catch((err) => {
          alert.error(err.message || "Failed to add new workshop.");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      apiRemote
        .createWorkshopProposal({
          title: formData.title,
          description: formData.description,
          username: user?.user_login || "",
          user_fullname: user?.display_name || "",
          user_email: user?.user_email || "",
          learning_objectives: formData.learning_objective,
          image: formData.image,
          date: formData.date,
          duration: formData.duration,
          category_id: formData.category_id,
          max_attendee: formData.max_attendee,
          format: formData.format,
          target_audience: formData.target_audience,
          topics: formData.topics,
          event_link: formData.event_link,
        })
        .then((data) => {
          if (data.code == "201") {
            alert.success(data.message);
            setFormData(initialFormData);
            setSelectedImage(null); // Clear selected image
            setAiImagePrompt("");
          } else {
            alert.error(data.message);
          }
        })
        .catch((err) => {
          alert.error(err.message || "Failed to add new workshop.");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    // alert.success("Workshop Published!")
    if (
      !formData.title ||
      !formData.description ||
      !formData.date ||
      !formData.topics ||
      formData.topics.length === 0 ||
      !formData.duration ||
      !formData.format ||
      !formData.category_id ||
      !formData.max_attendee ||
      !formData.learning_objective ||
      !formData.target_audience
    ) {
      alert.error("Please fill in all required information");
      return;
    }

    if (!formData.image) {
      alert.error("Please upload an image for the new workshop.");
      return;
    }

    setIsSavingDraft(true);
    try {
      const dataToSend = { ...formData, status: "draft" };
      console.log("Saving draft with data:", dataToSend);

      // Thay thế bằng API endpoint phù hợp của bạn
      // Ví dụ: apiRemote.saveDraftWorkshop(dataToSend)
      // Hiện tại, bạn có thể gọi lại createWorkshop và gán status là 'draft'
      const response = await apiRemote.createWorkshop(dataToSend);

      if (response.code === "201") {
        alert.success("Draft saved successfully!");
        setFormData(initialFormData);
        setSelectedImage(null); // Clear selected image
        setAiImagePrompt("");
        // Có thể navigate hoặc giữ nguyên trang
      } else {
        alert.error(response.message || "Failed to save draft.");
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      alert.error("Failed to save draft. Please try again.");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const addCategory = () => {
    if (newCategory.trim()) {
      setCategories([...categories, newCategory.trim()]);
      setNewCategory("");
    }
  };

  const deleteCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  // --- AI Integration Functions ---

  const handleGenerateSummary = async () => {
    if (!formData.title.trim()) {
      alert.warning(
        "Please enter a Workshop Title first to generate a summary."
      );
      return;
    }
    setIsGeneratingSummary(true);
    try {
      const response = await apiRemote.generateSummary({
        title: formData.title,
        brief_summary: formData.description
      });
      if(response.data.summary){
        setFormData((prev) => ({
          ...prev,
          description: response.data.summary,
        }));
      }else{
        alert.error("We're sorry, we are currently experiencing an issue. Please try again in a few minutes.")
      }
      
    } catch (error) {
      console.error("Error generating summary:", error);
      alert.error("Failed to generate summary. Please try again.");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleGenerateObjectives = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      alert.warning(
        "Please enter a Workshop Title and Brief Summary first to generate learning objectives."
      );
      return;
    }
    setIsGeneratingObjectives(true);
    try {
      const response = await apiRemote.generateLearningObjective({
        title: formData.title,
        summary: formData.description,
        learning_objectives: formData.learning_objective
      });
      if (response.data && Array.isArray(response.data.learning_objectives)) {
        // Format the array into a bulleted string for the TextField
        const formattedObjectives = response.data.learning_objectives
          .map((obj: string) => `- ${obj}`) // Add a bullet point to each objective
          .join("\n"); // Join them with a newline
        setFormData((prev) => ({
          ...prev,
          learning_objective: formattedObjectives,
        }));
      } else {
        alert.error("Invalid response format for learning objectives.");
      }
    } catch (error) {
      console.error("Error generating objectives:", error);
      alert.error("Failed to generate learning objectives. Please try again.");
    } finally {
      setIsGeneratingObjectives(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!aiImagePrompt.trim()) {
      alert.warning("Please enter a prompt to generate an image.");
      return;
    }
    setIsGeneratingImage(true);
    try {
      const response = await apiRemote.generateImage({
        prompt: aiImagePrompt,
      });
      setSelectedImage(response.data.image_urls[0]);
      setFormData((prev) => ({
        ...prev,
        image: response.data.image_urls[0],
      }));
    } catch (error) {
      console.error("Error generating image:", error);
      alert.error("Failed to generate image. Please try again.");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validExtensions.includes(file.type)) {
        alert.error('Only accept files in .jpg, .jpeg, .png formats');
        return;
      }
    }

    if (!file) return;

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    if (file && file.size > maxFileSize) {
      alert.error('File size is too large. Please select a file smaller than 5MB.');
      event.target.value = ''; // Clear the input
      return;
    }

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  const handleTopicsChange = (
    event: any,
    newValue: (string | { id: number; name: string })[]
  ) => {
    const formattedTopics = newValue.map((topic) =>
      typeof topic === "string" ? { id: Date.now(), name: topic } : topic
    );
    // Cập nhật formData.topics thay vì selectedTopics
    setFormData((prev) => ({
      ...prev,
      topics: formattedTopics,
    }));
  };

  // File upload handler
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
  
    // Tạo danh sách file mới kết hợp file cũ
    setFormData(prev => ({
      ...prev,
      attached_documents: [...prev.attached_documents, ...newFiles]
    }));
  };
  
  // Remove file
  const handleRemoveFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attached_documents: prev.attached_documents.filter((_, i) => i !== index)
    }));
  };

  const handleAddGuest = () => {
    setFormData((prev) => ({
      ...prev,
      hosts: [
        ...prev.hosts,
        {
          host_name: "",
          host_email: "",
          host_organization: "",
          host_bio: "",
        },
      ],
    }));
  };

  const handleRemoveGuest = (index: number) => {
    setFormData((prev) => {
      const newHosts = [...prev.hosts];
      newHosts.splice(index, 1);
      return { ...prev, hosts: newHosts };
    });
  };

  return (
    <Container maxWidth="lg">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("..")}
        sx={{ mb: 3 }}
      >
        Back to All Workshops
      </Button>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight={500}>
          {isAdminOrOrg ? "Create Official Workshop" : "Propose a Workshop"}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {isAdminOrOrg
            ? "This will be published as an official Pythaverse event, pre-approved and featured."
            : "Have an idea for a workshop you'd like to share with the community? We'd love to hear it! Fill out the short form below to submit your idea for review."}
        </Typography>
      </Box>

      <Card>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Section 1: Workshop Content */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                1
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Workshop Content
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Workshop Title"
              placeholder="e.g., The Official TCC Debrief"
              required
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
            />

            <TextField
              fullWidth
              label="Brief Summary"
              required
              placeholder="A short, catchy summary for the workshop card..."
              multiline
              value={formData.description}
              minRows={3} // Bắt đầu với 3 dòng
              maxRows={10}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              InputProps={{
                endAdornment: (
                  <Button
                    startIcon={
                      isGeneratingSummary ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateSummary}
                    disabled={isGeneratingSummary || !formData.title.trim()}
                  >
                    {isGeneratingSummary ? "Drafting..." : "Draft with AI"}
                  </Button>
                ),
              }}
            />

            {/* <EditToggle
              label="Brief Summary"
              data={{
                value: formData.description || "",
                type: EditType.EDITOR,
              }}
              isEdit={true}
              required={true}
              onChange={(value: string) => {
                const trimmed = value?.trimStart() || "";
                setFormData(prev => ({
                  ...prev,
                  description: trimmed
                }))
              }}
              btn={<Button
                startIcon={isGeneratingSummary ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                size="small"
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary || !formData.title.trim()}
              >
                {isGeneratingSummary ? "Drafting..." : "Draft with AI"}
              </Button>}
            /> */}

            <TextField
              fullWidth
              label="Learning Objectives"
              required
              placeholder="- Identify 3-5 practical AI tools..."
              multiline
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              minRows={5} // Bắt đầu với 5 dòng
              maxRows={15}
              value={formData.learning_objective}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  learning_objective: e.target.value,
                }))
              }
              InputProps={{
                endAdornment: (
                  <Button
                    startIcon={
                      isGeneratingObjectives ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateObjectives}
                    disabled={
                      isGeneratingObjectives ||
                      !formData.title.trim() ||
                      !formData.description.trim()
                    }
                  >
                    {isGeneratingObjectives ? "Generating..." : "Generate"}
                  </Button>
                ),
              }}
            />

            {/* <EditToggle
              label="Learning Objectives"
              data={{
                value: formData.learning_objective || "",
                type: EditType.EDITOR,
              }}
              isEdit={true}
              required={true}
              onChange={(value: string) => {
                const trimmed = value?.trimStart() || "";
                setFormData(prev => ({
                  ...prev,
                  learning_objective: trimmed
                }))
              }}
              btn={<Button
                startIcon={isGeneratingObjectives ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                size="small"
                onClick={handleGenerateObjectives}
                disabled={isGeneratingObjectives || !formData.title.trim() || !formData.description.trim()}
              >
                {isGeneratingObjectives ? "Generating..." : "Generate"}
              </Button>}
            /> */}

            <TextField
              fullWidth
              label="Target Audience"
              required
              placeholder="e.g., K-12 Science Teachers, School Administrators..."
              multiline
              rows={2}
              sx={{
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              value={formData.target_audience}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  target_audience: e.target.value,
                }))
              }
            />
          </CardContent>

          {/* Section 2: Host Details */}
          {isAdminOrOrg && (
            <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  2
                </Box>
                <Typography variant="h4" fontWeight={500}>
                  Host Details
                </Typography>
              </Box>

              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <RadioGroup
                  row
                  value={formData.host_type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      host_type: e.target.value,
                    }))
                  }
                >
                  <FormControlLabel
                    value="pythaverse"
                    control={<Radio />}
                    label="🏢 Pythaverse Educator"
                  />
                  <FormControlLabel
                    value="guest"
                    control={<Radio />}
                    label="🎤 Invited Guest"
                  />
                </RadioGroup>
              </FormControl>

              {formData.host_type == "pythaverse" ? (
                <Autocomplete
                  multiple 
                  fullWidth
                  options={educators}
                  getOptionLabel={(option) => option.display_name + ' (' + option.user_email + ')'} // Display educator's name
                  filterOptions={(options, state) => {
                    // Lọc các lựa chọn để loại bỏ những người đã được chọn
                    const filteredOptions = options.filter(
                      (option) =>
                        !selectedEducators.some(
                          (selected) => selected.user_email === option.user_email
                        )
                    );
                    return filteredOptions;
                  }}
                  onInputChange={(event, newInputValue) => {
                    setSearchEducators(newInputValue); // Update search term to trigger debounced search
                  }}
                  onChange={(event, newValue) => {
                    setSelectedEducators(newValue); // Set the selected educator object
                  }}
                  value={selectedEducators} // Controlled component: display the selected educator
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and add Educators"
                      placeholder="Search and add Educators..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {debouncedSearchTerm &&
                            educators.length === 0 &&
                            !selectedEducators ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  // Optional: Render a custom option
                  renderOption={(props, option) => (
                    <li {...props}>
                      <ListItemText
                        primary={option.display_name}
                        secondary={option.user_email}
                      />
                    </li>
                  )}
                  noOptionsText={
                    searchEducators
                      ? "No educators found."
                      : "Start typing to Search and add Educatorss."
                  }
                />
              ) : (
                <Box>
                  {formData.hosts.map((host, index) => (
                    <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                      <Grid item xs={10}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Guest's Full Name"
                              value={host.host_name}
                              onChange={(e) => {
                                const newHosts = [...formData.hosts];
                                newHosts[index].host_name = e.target.value;
                                setFormData({ ...formData, hosts: newHosts });
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Guest's Email"
                              value={host.host_email}
                              onChange={(e) => {
                                const newHosts = [...formData.hosts];
                                newHosts[index].host_email = e.target.value;
                                setFormData({ ...formData, hosts: newHosts });
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Guest's Title & Organization"
                              value={host.host_organization}
                              onChange={(e) => {
                                const newHosts = [...formData.hosts];
                                newHosts[index].host_organization = e.target.value;
                                setFormData({ ...formData, hosts: newHosts });
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Guest's Professional Bio"
                              multiline
                              rows={3}
                              value={host.host_bio}
                              onChange={(e) => {
                                const newHosts = [...formData.hosts];
                                newHosts[index].host_bio = e.target.value;
                                setFormData({ ...formData, hosts: newHosts });
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={2}>
                          <IconButton onClick={() => handleRemoveGuest(index)} color="error">
                              <DeleteIcon />
                          </IconButton>
                      </Grid>
                    </Grid>
                    
                  ))}
                  <Button onClick={handleAddGuest}>Add Guest</Button>
                </Box>
              )}
            </CardContent>
          )}

          {/* Section 3: Logistics & Settings */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {isAdminOrOrg ? 3 : 2}
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Logistics & Settings
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Workshop Date"
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (in minutes)"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  placeholder="e.g., 60"
                  required
                  value={formData.duration}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    // Chỉ cập nhật giá trị nếu nó không âm
                    if (value >= 0) {
                      setFormData((prev) => ({
                        ...prev,
                        duration: value,
                      }));
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Format <span style={{color: "red"}}>*</span></InputLabel>
                  <Select
                    defaultValue="online"
                    label="Format"
                    required
                    value={formData.format}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        format: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="in-person">In-Person</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category <span style={{color: "red"}}>*</span></InputLabel>
                  <Select
                    defaultValue="ai"
                    label="Category"
                    required
                    value={formData.category_id}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        category_id: e.target.value,
                      }))
                    }
                  >
                    {categories.map((category, index) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* <Button startIcon={<EditIcon />} size="small" onClick={() => setCategoryModalOpen(true)} sx={{ mt: 1 }}>
                  Edit Categories
                </Button> */}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Attendee Limit"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  placeholder="Leave blank for unlimited"
                  required
                  value={formData.max_attendee}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    // Chỉ cập nhật giá trị nếu nó không âm
                    if (value >= 0) {
                      setFormData((prev) => ({
                        ...prev,
                        max_attendee: value,
                      }));
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Live Event Link"
                  type="text"
                  // required
                  value={formData.event_link}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      event_link: e.target.value,
                    }))
                  }
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={topics}
                  getOptionLabel={(option: any) => option.name}
                  value={formData.topics} // Thay thế selectedTopics bằng formData.topics
                  isOptionEqualToValue={(option, value) =>
                    option.id == value.id
                  }
                  onChange={handleTopicsChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={
                        <>
                          Workshop Tags <span style={{ color: "red" }}>*</span>
                        </>
                      }
                      placeholder="Type a tag and press Enter"
                      // Thêm helperText ở đây để hướng dẫn người dùng
                      helperText="Add tags to help educators discover your workshop"
                      sx={{
                        '& .MuiFormHelperText-root': {
                          // fontWeight: 'bold', // Ví dụ: in đậm chữ
                          fontSize: '0.9rem', // Ví dụ: tăng kích thước chữ
                          // Bạn có thể thêm các thuộc tính CSS khác tùy ý
                        },
                      }}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Video Link"
                  type="text"
                  // required
                  value={formData.video_link}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      video_link: e.target.value,
                    }))
                  }
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>

          {/* Section 4: Cover Image */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {isAdminOrOrg ? 4 : 3}
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Cover Image
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Generate Image with AI"
              placeholder="e.g., 'A modern classroom with glowing data visualizations'"
              value={aiImagePrompt}
              onChange={(e) => setAiImagePrompt(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <Button
                    startIcon={
                      isGeneratingImage ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateImage}
                    disabled={isGeneratingImage || !aiImagePrompt.trim()} // Disable if prompt is empty or generating
                  >
                    {isGeneratingImage ? "Generating..." : "Generate"}
                  </Button>
                ),
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                (or)
              </Typography>
              <Button
                component="label" // Use label to trigger file input
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                Upload image manually
                <input
                  type="file"
                  hidden
                  accept="image/*" // Accept only image files
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                {selectedImage ? (
                  <Box
                    component="img"
                    src={selectedImage}
                    alt="Selected Cover Image"
                    sx={{
                      width: "100%",
                      borderRadius: 1,
                      border: 3,
                      borderColor: "primary.main",
                      minHeight: 300,
                      maxHeight: 700,
                      objectFit: "fill",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: 169, // Maintain aspect ratio roughly
                      borderRadius: 1,
                      border: 3,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.100",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">No image selected</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>

          {/* Attached Documents */}
          {isAdminOrOrg && (
            <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  {isAdminOrOrg ? 5 : 4}
                </Box>
                <Typography variant="h4" fontWeight={500}>
                  Resources
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*,application/pdf,.doc,.docx,.zip"
                  style={{ display: 'none' }}
                  id="upload-documents"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                />
                <label htmlFor="upload-documents">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                  >
                    Upload documents
                  </Button>
                </label>
              </Box>
            
              {formData.attached_documents && formData.attached_documents.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Uploaded files:
                  </Typography>
                  {formData.attached_documents.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <Typography variant="body2">{file instanceof File ? file.name : file.file_name}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          )}
          

          {/* Actions */}
          <Box
            sx={{
              p: 3,
              bgcolor: "grey.50",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {isAdminOrOrg && (
              <Button variant="outlined" disabled={isSavingDraft} onClick={handleSaveDraft}>
                {isSavingDraft ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Save as Draft"
                )}
              </Button>
            )}
            
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : user?.is_admin || user?.is_org ? (
                "Publish Official Workshop"
              ) : (
                "Publish Propose Workshop"
              )}
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Category Management Modal */}
      <Dialog
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Workshop Categories</DialogTitle>
        <DialogContent>
          <List
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 200,
              overflow: "auto",
            }}
          >
            {categories.map((category, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => deleteCategory(category.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={category.name} />
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <TextField
              fullWidth
              placeholder="Add new category name..."
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            />
            <Button variant="contained" onClick={addCategory}>
              Add
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryModalOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
